import type { EntityAvatarEntityType } from "./entityAvatar.types";
import { isPostBackedKind } from "util/entityMetadata";

/**
 * Keys where list/embedded payloads often carry denormalized data that a bare
 * `GET /api/posts/:id` response may omit or leave empty.
 */
const GAP_FILL_KEYS: string[] = [
  "subject_cname",
  "topic_cname",
  "cname",
  "name",
  "entity_name",
  "username",
  "display_name",
  "user_id",
  "identity_mode",
  "issue_id",
  "entity_properties",
  "issue_properties",
  "related_issue",
  "related_issues",
  "placement",
  "entity_type",
  "rel_type",
  "influence_id",
  "properties",
  "properties_public",
  "attributes",
  // Context flag set by callers that surface a post inside a comment
  // chrome (e.g. a teabate-argument inline header on the issue
  // Discussion). Detail responses never carry this; the inline caller
  // is the only signal. Preserving it lets EntityAvatar pick the
  // comment-context branch (Teabate badge + parent → post layout).
  "is_comment",
];

function isEmptyScalar(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim().length === 0;
  }
  return false;
}

/**
 * Merge a detail response with the inline entity from parents (e.g. feed cards).
 * Detail wins on conflicts; inline fills gaps when the detail payload dropped fields.
 *
 * @param detail - Normalized row from `getEntityDetails` / React Query.
 * @param inline - Entity passed from the parent (feed list, props).
 * @param entityType - Avatar / domain type.
 */
export function mergeEntityDetailWithInline(
  detail: Record<string, unknown>,
  inline: Record<string, unknown>,
  entityType: EntityAvatarEntityType
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...detail };

  for (const key of GAP_FILL_KEYS) {
    if (isEmptyScalar(merged[key]) && !isEmptyScalar(inline[key])) {
      merged[key] = inline[key];
    }
  }

  if (isPostBackedKind(entityType)) {
    const title =
      typeof merged.title === "string" ? merged.title.trim() : "";
    /** Do not copy post `title` into `name` for impact/teabate — that is not the issue headline. */
    if (entityType !== "impact" && entityType !== "teabate") {
      const name =
        typeof merged.name === "string" ? merged.name.trim() : "";
      if (!name && title) {
        merged.name = merged.title;
      }
    }
    if (isEmptyScalar(merged.entity_name) && title) {
      merged.entity_name = merged.title;
    }
  }

  return merged;
}
